import { of, forkJoin, throwError } from 'rxjs'
import { switchMap, mergeMap, catchError, tap } from 'rxjs/operators'
import ConfigHelper from 'framework/helpers/config'
import * as factories from '../factories'
import Constants from 'api-constants'
import VoltError from 'VoltError'
import DataHelper from 'framework/helpers/data'
import UfinityAuthApi from 'services/ufinity/AuthApi'
import NagraAuthApi from 'services/nagra/AuthApi'
import { OAuthDeviceFlowStatus, Profile } from 'models'
import MockLogger from 'MockLogger'
import CommonApi from 'services/common'

/**
 * Starhub is a virtual Backend as it does not really exist.
 * It reflect the hybridization between Ufinity (OAuth + OTT GTW) and Nagra Backends
 */
export default class AuthApi extends UfinityAuthApi {
    constructor(config, otherApis = {}) {
        super(config)
        this.nagraAuthApi = new NagraAuthApi(config, otherApis)
        this.gatewayApi = otherApis.gatewayApi
        // Used by STB only rather than the serial number for authentication
        // For other devices should be undefined
        this.casn = undefined
        // Add another logger for the parent for better readability
        this.shLogger = (config.logger || MockLogger)
            .createChildInstance('starhub')
            .createChildInstance('auth')
    }

    /**
     * Retrieves the profile by using authentication token in cache
     * Called systematically by the UI at boot for seeking after authentication skipping authentication screen
     * If the token is missing or invalid, then throw an error to force the UI for authentication
     * @returns {Observable<Profile>}
     */
    getProfile({ deviceData } = {}) {
        // Should follow this specific order. This flow is really sensitive for Starhub
        // As they encountered a lot of issues with their previous UI supplier
        // This is logs are added by anticipation here :)
        this.shLogger.info('authenticating against UFINITY...')

        return DataHelper.getInstance()
            .readData([DataHelper.STORE_KEY.CASN, DataHelper.STORE_KEY.STB_MIGRATION_LOGIN]) // Could be needed for re-authenticating silently on STB
            .pipe(
                mergeMap(() => {
                    // Store CASN locally
                    this._setCasn(
                        deviceData?.casn ||
                            DataHelper.getInstance().getData(DataHelper.STORE_KEY.CASN)
                    )

                    return super.authenticate().pipe(
                        mergeMap(() => {
                            this.shLogger.info(
                                'authentication against UFINITY succeeded ! Trying NAGRA...'
                            )
                            return this.nagraAuthApi.authenticate().pipe(
                                mergeMap(() => {
                                    this.shLogger.info(
                                        'authentication from cache against NAGRA succeeded ! Parsing profile to finish the process...'
                                    )
                                    return this.fetchProfile()
                                }),
                                catchError((error = {}) => {
                                    this.shLogger.error(
                                        'authentication from cache against NAGRA failed !',
                                        error.code
                                    )
                                    this.shLogger.info(
                                        'Trying to RE-authenticate silently on NAGRA using ufinity oauth before throwing error...'
                                    )
                                    return this._nagraLogin({ firstLogin: false, deviceData }).pipe(
                                        mergeMap(() => {
                                            this.shLogger.info(
                                                'Silent RE-authentication from NAGRA succeeded ! Parsing profile to finish the process...'
                                            )
                                            return this.fetchProfile()
                                        }),
                                        catchError((err) => {
                                            this.shLogger.error(
                                                'silent  authentication against NAGRA failed !'
                                            )
                                            return throwError(
                                                new VoltError(VoltError.codes.LOGIN_REQUIRED, {
                                                    inheritedError: err,
                                                })
                                            )
                                        })
                                    )
                                })
                            )
                        }),
                        catchError((error) => {
                            this.shLogger.error(
                                'authentication from cache against UFINITY failed !'
                            )
                            if (this.config.platform === Constants.platform.androidTvStb) {
                                return this._autoLoginForMigrationSTB({ deviceData }).pipe(
                                    mergeMap((isSucceeded) => {
                                        if (!isSucceeded) return throwError(error)

                                        return this.getProfile({ deviceData })
                                    })
                                )
                            }

                            return throwError(error)
                        })
                    )
                })
            )
    }

    /**
     * Fetch the profile and Merge Nagra and Ufinity Profile into one
     * Also sends "Notify User Login" request by {@link GatewayApi}
     *
     * @returns {Observable<Profile>}
     */
    fetchProfile() {
        this.shLogger.info('fetching ufinity/nagra profiles...')
        return forkJoin({
            ufinityProfile: super.fetchProfile(),
            nagraProfile: this.nagraAuthApi.fetchProfile(),
        }).pipe(
            mergeMap(({ ufinityProfile, nagraProfile }) => {
                const profile = factories.profileFactory(ufinityProfile, nagraProfile)
                CommonApi.getInstance().setCurrentProfile(profile)
                return this.gatewayApi.notifyUserLogin().pipe(switchMap(() => of(profile)))
            })
        )
    }

    /**
     * Username/Password Login
     * @param {Object} data
     * @param {String} data.username
     * @param {String} data.password
     * @param {Object} [data.deviceData] optional device data (brand, model, serial number, casn, etc...)
     * @returns {Observable<String>} Constants.authenticationStatus values
     */
    login(data = {}) {
        this.shLogger.info('login against ufinity/nagra...')
        const { deviceData = {} } = data

        this._setCasn(deviceData.casn)
        return super.login(data).pipe(
            // Login also on Nagra
            switchMap(() => this._nagraLogin({ firstLogin: true, deviceData: data.deviceData }))
        )
    }

    /**
     * Logout
     */
    logout({ skipRevocation = false } = {}) {
        this.shLogger.info(`logout against ufinity/nagra... Skip Revocation call ${skipRevocation}`)

        return forkJoin({
            ufinity: super.logout({ skipRevocation }),
            nagra: this.nagraAuthApi.logout({ skipRevocation }),
        }).pipe(switchMap(() => of(true)))
    }

    /**
     * Please refer to parent method for documentation
     */
    loginWithSecondaryDevice({ deviceId, deviceData = {} } = {}) {
        this.shLogger.info('DAG login against ufinity...')

        this._setCasn(deviceData.casn)
        return super.loginWithSecondaryDevice({
            // Presence of casn means STB login, otherwise use deviceId (for VSTB)
            deviceId: this._getCasn() || deviceId,
        })
    }

    /**
     * Please refer to parent method for documentation
     */
    isLoginWithSecondaryDevice({ deviceData = {} }) {
        this.shLogger.info('DAG polling against ufinity...')

        return super.isLoginWithSecondaryDevice().pipe(
            mergeMap((oauthStatus) => {
                // When user is logged-in using DAG, then login to Nagra
                const isLogin =
                    OAuthDeviceFlowStatus.OAUTH_STATUS.LOGIN_SECONDARY_DEVICE === oauthStatus.status
                if (isLogin) {
                    return this._nagraLogin({ firstLogin: true, deviceData }).pipe(
                        mergeMap((loginStatus) => {
                            if (
                                Constants.authenticationStatus
                                    .SUCCEEDED_REQUIRE_LOGIN_TV_SERVICE === loginStatus
                            ) {
                                oauthStatus = oauthStatus.update({
                                    status: OAuthDeviceFlowStatus.OAUTH_STATUS
                                        .LOGIN_SECONDARY_DEVICE_REQUIRE_LOGIN_TV_SERVICE,
                                })
                            }
                            return of(oauthStatus)
                        }),
                        catchError((error) => {
                            this.shLogger.error('authentication failed against NAGRA')
                            oauthStatus = oauthStatus.update({
                                status: OAuthDeviceFlowStatus.OAUTH_STATUS.ERROR,
                                error,
                            })
                            return of(oauthStatus)
                        })
                    )
                }
                return of(oauthStatus)
            })
        )
    }

    /**
     * Returns the list of tv services (similar to account) to select to be fully logged in
     * @returns {Observable<Array<TvService>>}
     */
    getTvServices() {
        this.shLogger.info(`Retrieving list of TV services for this HUB ID...`)

        const essoId = DataHelper.getInstance().getData(DataHelper.STORE_KEY.ESSOID)
        if (!essoId) {
            this.shLogger.info('EssoID not cached, retrieve it before...')
            return super
                .fetchProfile(false)
                .pipe(
                    switchMap((profile) =>
                        this.gatewayApi.retrieveMappingBetweenHubIdAndTvId(profile.userId)
                    )
                )
        }
        return this.gatewayApi.retrieveMappingBetweenHubIdAndTvId(essoId)
    }

    /**
     * Login using tv service within (Second step login)
     * @param {Object} data
     * @param {Boolean} [data.useCache=false] indicates that it tries to login from cache
     * @param {Object} data.serviceId
     * @param {Object} data.deviceData
     * @returns {Observable<Boolean>} True if success otherwise return a boolean
     */
    loginTvService({ serviceId, deviceData, useCache = false } = {}) {
        this.shLogger.info(`Processing TV ID Login on Nagra...`)
        return this.nagraAuthApi.login({
            accountId: serviceId,
            oauthToken: DataHelper.getInstance().getPrimaryAccessToken(),
            deviceData,
            useCache,
        })
    }

    /**
     * Login on Nagra both for STB and VSTB (open device)
     * @param {Object} data
     * @param {Boolean} data.firstLogin indicates that it corresponds to a login done manually from the ui
     * @param {String} data.deviceData
     * @returns {Observable<SUCCEEDED|SUCCEEDED_REQUIRE_LOGIN_TV_SERVICE>} Constants.authenticationStatus values
     */
    _nagraLogin({ firstLogin = true, deviceData = {} } = {}) {
        const casn = this._getCasn()
        const { forceCasn } = ConfigHelper.getInstance().getConfig('nagra')
        const myCasn = forceCasn || casn

        // -- 1 -- MANAGED STB LOGIN
        if (myCasn) {
            this.shLogger.info(
                `CASN available (forceCasn:${forceCasn})... Triggering CASN Login on Nagra !`
            )

            return this.nagraAuthApi
                .loginWithSecureId({
                    secureId: myCasn,
                    oauthToken: DataHelper.getInstance().getPrimaryAccessToken(),
                })
                .pipe(
                    switchMap(() => of(Constants.authenticationStatus.SUCCEEDED)),
                    tap(() => {
                        DataHelper.getInstance().storeData([[DataHelper.STORE_KEY.CASN, myCasn]])
                    })
                )
        }

        this.shLogger.info('CASN missing... Require a two steps login for OPEN DEVICE !')
        // -- 2 -- OPEN DEVICE LOGIN
        return super.fetchProfile(false).pipe(
            switchMap((profile) => {
                switch (profile.userAccountType) {
                    case Profile.USER_ACCOUNT_TYPE.LITE:
                        // Login with ESSOID for Lite Hub ID account
                        return this.loginTvService({
                            serviceId: profile.userId,
                            deviceData,
                            useCache: !firstLogin,
                        })

                    default:
                    case Profile.USER_ACCOUNT_TYPE.MAIN:
                        if (firstLogin) {
                            const { debugForceTvServiceAutoSelection = false } =
                                ConfigHelper.getInstance().getConfig('ufinity')
                            if (debugForceTvServiceAutoSelection) {
                                this.shLogger.info(
                                    '[DEBUG] SHOULD NOT BE ACTIVATED IN PRODUCTION.. Autoselection of the first TV Service'
                                )
                                return this.getTvServices().pipe(
                                    mergeMap((TvServices) => {
                                        const firstService = TvServices.find(Boolean)
                                        return this.loginTvService({
                                            serviceId: firstService && firstService.serviceId,
                                            deviceData,
                                            useCache: !firstLogin,
                                        })
                                    })
                                )
                            }

                            this.shLogger.info('First Login requiring user explicit selection')
                            return of(
                                Constants.authenticationStatus.SUCCEEDED_REQUIRE_LOGIN_TV_SERVICE
                            )
                        }

                        this.shLogger.info(
                            'Not a first login, trying to recover using last selected account from cache...'
                        )

                        // Login with TVID for MAIN Hub ID account
                        return this.loginTvService({
                            serviceId: DataHelper.getInstance().getData(
                                DataHelper.STORE_KEY.ACCOUNT_ID
                            ),
                            deviceData,
                            useCache: !firstLogin,
                        })
                }
            })
        )
    }

    /**
     * Auto Login to handle migration of users for STB
     * @param {Object} data
     * @param {Object} data.deviceData
     * @returns {Observable<Boolean>} True if success otherwise return a boolean
     */
    _autoLoginForMigrationSTB({ deviceData } = {}) {
        if (!deviceData?.casn) {
            this.shLogger.info(
                `[MIGRATION LOGIN] Missing CASN information, cannot perform migration Login`
            )
            return of(false)
        }

        const hasAlreadyPerformMigrationLogin = DataHelper.getInstance().getData(
            DataHelper.STORE_KEY.STB_MIGRATION_LOGIN
        )
        if (hasAlreadyPerformMigrationLogin) {
            this.shLogger.info(
                `[MIGRATION LOGIN] Migration Login already performed. Do not allow it again`
            )
            return of(false)
        }

        if (this._hasAlreadyTryMigrationLogin) {
            this.shLogger.info(
                `[MIGRATION LOGIN] Migration Login already tried from first boot. Do not perfom it again to avoid unecessary rebounds`
            )
            return of(false)
        }
        this._hasAlreadyTryMigrationLogin = true

        if (this.config.platform !== Constants.platform.androidTvStb) {
            this.shLogger.info(
                `[MIGRATION LOGIN] Non STB devices NOT ALLOWED to perform the migration from Accedo to IFS Launcher!`
            )
            return of(false)
        }

        const {
            stbMigration: {
                enable: migrationEnabled = false,
                dummyUsername,
                dummyPassword,
                clientId,
            } = {},
        } = ConfigHelper.getInstance().getConfig('ufinity')

        if (!migrationEnabled) {
            this.shLogger.info(`[MIGRATION LOGIN] Migration disabled from the config`)
            return of(false)
        }

        this.shLogger.info(
            `[MIGRATION LOGIN] STB devices ALLOWED to perform the migration from Accedo to IFS Launcher!`
        )

        const _wipeCacheThenFails$ = (_offset) => {
            // Clear Data and stop refresh token timer before firing error
            return this.logout({ skipRevocation: true }).pipe(switchMap(() => of(false)))
        }

        return this.login({
            username: dummyUsername,
            password: dummyPassword,
            deviceData,
        }).pipe(
            mergeMap(() => {
                return this.fetch({
                    url: `${this.config.urls.authUrl}/api/v1/getTokenByTVID`,
                    method: 'POST',
                    headers: {
                        'Content-Type':
                            Constants.httpHeader.ContentType.APPLICATION_WWW_FORM_URLENCODED,
                    },
                    body: {
                        access_token: DataHelper.getInstance().getPrimaryAccessToken(),
                        client_id: clientId,
                        target_client_id: this.config.clientId,
                        casn: this._getCasn(),
                        tvid: DataHelper.getInstance().getData(DataHelper.STORE_KEY.ACCOUNT_ID),
                    },
                    log: '[MIGRATION LOGIN] RETRIEVE TOKEN BY TV ID',
                }).pipe(
                    mergeMap(({ response = {} }) => {
                        if (!response || !response.access_token || !response.refresh_token) {
                            this.shLogger.error(
                                `[MIGRATION LOGIN] Missing Access Token or refresh token from the response. Migration Login failure`
                            )
                            return _wipeCacheThenFails$()
                        }
                        this.shLogger.info(
                            `[MIGRATION LOGIN] Migration Login performed successfully. Ufinity Access token recovered successfully`
                        )
                        return DataHelper.getInstance()
                            .storeData([
                                [DataHelper.STORE_KEY.PRIMARY_ACCESS_TOKEN, response.access_token],
                                [
                                    DataHelper.STORE_KEY.PRIMARY_ACCESS_TOKEN_EXPIRATION_TIME_MS,
                                    response.expires_in * 1000 + Date.now(),
                                ],
                                [
                                    DataHelper.STORE_KEY.PRIMARY_REFRESH_TOKEN,
                                    response.refresh_token,
                                ],
                                [
                                    DataHelper.STORE_KEY.PRIMARY_ACCESS_TOKEN_ISSUED_TIME_MS,
                                    Date.now(),
                                ],
                                [DataHelper.STORE_KEY.STB_MIGRATION_LOGIN, 'Yes'],
                            ])
                            .pipe(
                                mergeMap(() => {
                                    this.shLogger.info(
                                        `[MIGRATION LOGIN] Migration Login succeeded, try to DO the REGULAR Login from cache`
                                    )
                                    return of(true)
                                })
                            )
                    }),
                    catchError((error) => {
                        this.logger.warn(
                            '[MIGRATION LOGIN] Cannot retrieve the token from Ufinity Migration Login API... Process failed. Migration Login failed',
                            error
                        )
                        return _wipeCacheThenFails$()
                    })
                )
            }),
            catchError((LoginErrorMigration) => {
                this.shLogger.error(
                    '[MIGRATION LOGIN] Cannot Login with Dummy Account or Nagra CASN API. Migration Login failed !',
                    LoginErrorMigration
                )
                return _wipeCacheThenFails$()
            })
        )
    }

    /**
     * Store locally the CASN
     * @param {String} [casn] optional device data (brand, model, serial number, casn, etc...)
     */
    _setCasn(casn) {
        this.casn = casn
    }

    /**
     * Return the CASN
     * @returns {String} CASN
     */
    _getCasn() {
        return this.casn
    }
}
